import * as THREE from 'three';
import {
  MODEL_IDLE,
  modelFootprint,
  modelPosesAt,
  modelPupils,
  modelScale,
  modelSlugOf,
  replayModels,
  shortestTurn,
  spannendWalk,
  turnSeconds,
  type Lesson,
  type ModelAnimation,
  type ModelCatalogue,
  type ModelCircle,
  type ModelFacts,
  type ModelPose,
  type ModelReplay,
  type ModelStepReplay,
  type StepModel,
  type WorldCatalogue,
} from '@vrlearning/lesson-schema';
import {
  MODEL_SOUND,
  modelSoundDb,
  modelsWithEmitters,
  mouthPanning,
  type ModelEmitter,
  type ModelSoundBank,
  type Panning,
} from '../audio/model-sound.js';
import type { ModelLight } from '../look/model-light.js';
import { shown } from '../motion.js';
import { fitModel, setSeen, showBlob, type FittedModel, type LoadedModel } from './builder.js';
import { CLIP_START, ModelCues, callMark, crossed, soundOf } from './cues.js';
import { ModelPerformance, fadeFor, type Ground, type PerformancePose } from './performance.js';
import {
  ModelFade,
  SPANNEND,
  SpannendRun,
  calmNearest,
  comingClose,
  lecternVeiled,
  spannendPartOf,
  type SpannendPhase,
} from './spannend.js';

/**
 * Every library model of a lesson as it plays (ADR-043; spec "Engine
 * behaviour"): its GLB in its object's group, a mixer each, the step's
 * entries played by `ModelPerformance`, its footsteps, calls and breath, the
 * Spannend part with its warning and "Sla over", and the lectern's veil.
 *
 * The place of a model object is this runtime's, not SceneMotion's (which
 * still shows, hides and highlights it): the group stands where the model
 * is, the GLB and the invisible box the pupil points at turn with it, and the
 * name stays upright above it.
 *
 * Step entry: forward by one, the step's entries play from where the models
 * are -- after a 0.3 s fade into the step before's calm end state when a
 * Spannend part left one elsewhere; anything else (a jump, back, joining)
 * puts every model at its calm end state at once. The teacher's pause and a
 * teleport's fade hold everything, sound included.
 *
 * A model going round (ADR-054) follows lesson-schema's ring for its step; a loop goes on through steps
 * that do not name it, a step that names it fades it (and only it) to the ring's start first, and a jump,
 * Back, a joiner or Sla over starts it there.
 */

const DEG = Math.PI / 180;
/** Seconds the class time may put a ring's lap from where it is before it fades there, not jumps. */
const CATCH_UP = 2;

/**
 * The class's shared time, as this headset hears it (docs/05, `session.state`'s `clock`; ADR-054 amended
 * 20 Oct 2026): what a model going round keeps to, so every headset in a class shows it at the same
 * point of its lap.
 */
export type ClassTime = {
  /** Seconds of the class's running time now (the teacher's pause stops it), as this headset estimates. */
  readonly now: number;
  /**
   * The class's run of steps up to the one it is on, each with the class time it was entered: the
   * first was put on at once (a jump, or the session's start; the lesson's first step plays), every
   * later one came forward by one.
   */
  readonly run: readonly { readonly index: number; readonly at: number }[];
};

/** What the runtime moves of a lesson object. */
export type ModelObject = {
  readonly group: THREE.Object3D;
  /** The invisible box the pupil points at (scene.ts); turned with the model. */
  readonly body: THREE.Object3D;
};

/** What the lectern is told: the warning, the part, Sla over, the end. */
export type SpannendEvent = {
  readonly phase: SpannendPhase | 'none';
  /** The labels of what comes close, for the warning. */
  readonly labels: readonly string[];
};

export type ModelRuntimeInput = {
  readonly lesson: Lesson;
  readonly catalogue: ModelCatalogue;
  /** The lesson's world as a catalogue: where its features are (framing's pupils, the replay). */
  readonly worlds?: WorldCatalogue | undefined;
  readonly objects: ReadonlyMap<string, ModelObject>;
  /** Fetches and parses a model's GLB; absent, nothing is drawn (the teacher's window may still). */
  readonly load?: ((facts: ModelFacts) => Promise<LoadedModel>) | undefined;
  readonly sound?: ModelSoundBank | undefined;
  /** Where the pupil's eyes are now, in the world. */
  readonly eye: (into: THREE.Vector3) => THREE.Vector3;
  /**
   * The land's height under (x, z) (the world's `ground`): a model stands on
   * it wherever it is and walks over it. Absent, the ground is flat at 0.
   */
  readonly ground?: ((x: number, z: number) => number) | undefined;
  /**
   * Whether the world's floor shows no sun shadow (an unlit 360° photo, package-world.ts): its
   * models then stand in their contact shadows. Asked every frame (the photo arrives late).
   */
  readonly unlitFloor?: (() => boolean) | undefined;
  /**
   * The world's model light (look/model-light.ts, ADR-100): every model is attached as its GLB is
   * fitted, its middle half its box's height above its feet. Absent (the studio, a 360° photo, AR),
   * the models are lit by what is there.
   */
  readonly light?: Pick<ModelLight, 'attach' | 'detach'> | undefined;
  readonly random?: () => number;
  readonly onSpannend?: (event: SpannendEvent) => void;
  /**
   * The class's shared time (ADR-054, 20 Oct 2026): absent, or undefined while there is none (solo
   * play, a test drive, a preview, Open in Quest, a free class), a ring is gone round on this
   * headset's own time.
   */
  readonly classTime?: (() => ClassTime | undefined) | undefined;
};

type Live = {
  readonly id: string;
  readonly facts: ModelFacts | undefined;
  readonly scale: number;
  /** How high above the land its box bottom rides (ADR-054's `above`); 0 on the land. */
  readonly above: number;
  readonly object: ModelObject;
  pose: PerformancePose;
  performance: ModelPerformance | undefined;
  /** The step whose entry put it on the ring it goes round now: what its lap keeps time from. */
  ringFrom: number | undefined;
  /**
   * Fading onto the class's lap (0.3 s out, 0.3 s in): the class time jumped by more than
   * `CATCH_UP` seconds (an API restart, a headset that roamed in a free class), so it is moved there
   * unseen rather than teleported along its ring.
   */
  catchUp: ModelFade | undefined;
  fitted: FittedModel | undefined;
  current: { clip: string; animation: ModelAnimation; action: THREE.AnimationAction } | undefined;
  fraction: number;
  readonly cues: ModelCues;
  feet: ModelEmitter | undefined;
  mouth: ModelEmitter | undefined;
};

const loops = (facts: ModelFacts | undefined, animation: ModelAnimation): boolean =>
  facts?.animations[animation]?.loop ?? animation === MODEL_IDLE;

const poseOf = (pose: ModelPose, facts: ModelFacts | undefined): PerformancePose => ({
  pos: [pose.pos[0], pose.pos[2]],
  turn: pose.turn,
  animation: pose.animation,
  timeScale: 1,
  loop: loops(facts, pose.animation),
});

/** More than 5 cm from where the calm path has it. */
const moved = (a: PerformancePose, b: PerformancePose): boolean =>
  Math.hypot(a.pos[0] - b.pos[0], a.pos[1] - b.pos[1]) > 0.05;

/** More than 5 cm or 2° from where the calm path has it: what a Spannend part may leave. */
const differs = (a: PerformancePose, b: PerformancePose): boolean =>
  moved(a, b) || Math.abs(shortestTurn(a.turn, b.turn)) > 2;

/** The same ring: centre, radius and direction within 5 cm (a loop going round and a calm pose's). */
const sameRing = (a: ModelCircle, b: ModelCircle): boolean =>
  a.direction === b.direction &&
  Math.abs(a.radius - b.radius) < 0.05 &&
  Math.hypot(a.centre[0] - b.centre[0], a.centre[1] - b.centre[1]) < 0.05;

export class ModelRuntime {
  readonly #input: ModelRuntimeInput;
  readonly #live = new Map<string, Live>();
  readonly #calm = new Map<number, ReadonlyMap<string, ModelPose>>();
  readonly #replays = new Map<number, ModelReplay>();
  readonly #pupils: ReturnType<typeof modelPupils> | undefined;
  readonly #eye = new THREE.Vector3();
  readonly #at = new THREE.Vector3();
  #index = -1;
  #run: SpannendRun | undefined;
  #part: readonly StepModel[] | undefined;
  #fade:
    | {
        fade: ModelFade;
        to: ReadonlyMap<string, ModelPose>;
        /** Only these fade (ADR-054: a loop going on is left alone); absent, every model. */
        only?: ReadonlySet<string>;
        /** These stand still at the swap (the step names them: its entries move them next). */
        still?: ReadonlySet<string>;
        then?: () => void;
      }
    | undefined;
  #veiled = false;
  /** ADR-084: per model, how near its calm path brings it this step (`calmNearest`); worked out once a step. */
  #calmNear = new Map<string, number | undefined>();
  /** A Spannend part played in the step just left: its end may stand anywhere. */
  #strayed = false;
  /** Each mouth's panning last frame (HRTF for the nearest four, with hysteresis). */
  #panning = new Map<string, Panning>();
  #clock = 0;
  /** "↻ Nog eens" played this step again for this pupil alone: its rings keep this headset's time. */
  #own = false;
  #disposed = false;

  #loaded: Promise<void> = Promise.resolve();

  constructor(input: ModelRuntimeInput) {
    this.#input = input;
    const { lesson, catalogue } = input;
    const hasModels = Object.values(lesson.objects ?? {}).some((object) =>
      object.kind.startsWith('model:'),
    );
    this.#pupils =
      hasModels && Object.keys(catalogue).length > 0
        ? modelPupils(lesson, catalogue, 1.6, input.worlds)
        : undefined;
    const declared = replayModels(lesson, { catalogue }).declared;
    const random = input.random ?? Math.random;
    for (const [id, object] of input.objects) {
      const kind = lesson.objects?.[id]?.kind;
      const slug = kind === undefined ? undefined : modelSlugOf(kind);
      const start = declared.get(id);
      if (slug === undefined || !start) continue;
      const facts = catalogue[slug];
      const live: Live = {
        id,
        facts,
        scale: facts ? modelScale(lesson.objects![id]!, facts) : 1,
        above: lesson.objects?.[id]?.above ?? 0,
        object,
        pose: poseOf(start, facts),
        performance: undefined,
        ringFrom: undefined,
        catchUp: undefined,
        fitted: undefined,
        current: undefined,
        fraction: CLIP_START,
        cues: new ModelCues(random),
        feet: undefined,
        mouth: undefined,
      };
      this.#live.set(id, live);
      this.#place(live);
    }
    this.#sound();
    this.#load();
  }

  /** The model objects it moves. */
  get ids(): string[] {
    return [...this.#live.keys()];
  }

  /** A model's pose now (tests, the HUD). */
  poseOf(id: string): PerformancePose | undefined {
    return this.#live.get(id)?.pose;
  }

  /**
   * The GLB clip a model plays now, by the GLB's own name; undefined while it is hidden, not loaded,
   * or holds a once-only clip's last frame (tests, the desktop harness).
   */
  playing(id: string): string | undefined {
    const live = this.#live.get(id);
    const action = live?.current?.action;
    if (!live || !action || !shown(live.object.group) || !action.isRunning()) return undefined;
    return live.current!.clip;
  }

  get phase(): SpannendPhase | undefined {
    return this.#run?.phase;
  }

  /** "Sla over" is offered: in a Spannend part's warning and during it. */
  get skippable(): boolean {
    return this.#run?.skippable === true;
  }

  /** The lectern fades out: a big model inside the clear space round the stage during a Spannend part. */
  get veiled(): boolean {
    return this.#veiled;
  }

  /** Every model's playing done: nothing walks, turns or plays once any more. */
  get settled(): boolean {
    return this.#allDone() && !this.#fade;
  }

  /**
   * A step begins. `forward`: the class moved on by one (or the lesson
   * starts), so its entries play; otherwise every model is put at the step's
   * calm end state at once. `spannend`: the switch as read at this entry.
   */
  enterStep(index: number, options: { forward: boolean; spannend: boolean }): void {
    this.#own = false;
    const strayed = this.#strayed;
    this.#strayed = false;
    this.#index = index;
    this.#run = undefined;
    this.#part = undefined;
    this.#fade = undefined;
    this.#veiled = false;
    this.#calmNear.clear();
    const step = this.#input.lesson.steps[index];
    const part = step ? spannendPartOf(step, options.spannend) : undefined;
    // ADR-054: a loop the step does not name goes on round its ring; one it names goes on until the
    // fade below takes it to the ring's start (its calm state).
    const named = new Set((part ?? step?.models ?? []).map((entry) => entry.object));
    // It goes on only when the calm path has it on that same ring too: a Spannend part replaces the
    // calm entries, so a loop the calm entries start or stop is taken to the calm state by the fade.
    const before = this.#calmAt(index - 1);
    const going = new Set<string>();
    for (const live of this.#live.values()) {
      const looping = options.forward ? live.performance?.looping : undefined;
      const calm = before.get(live.id)?.circle;
      if (looping && !named.has(live.id) && calm && sameRing(looping, calm)) going.add(live.id);
      else if (!looping) live.performance = undefined;
      live.catchUp = undefined;
      if (live.fitted) setSeen(live.fitted, 1);
    }
    // Said at once: a warning or "Sla over" of the step left never lingers through a fade.
    this.#tell();
    if (!options.forward) {
      this.#snap(this.#calmAt(index), false);
      return;
    }
    const begin = (): void => {
      if (part) {
        this.#part = part;
        this.#run = new SpannendRun();
        this.#tell();
        return;
      }
      this.#perform(step?.models ?? []);
      this.#tell();
    };
    // A model left elsewhere -- by a Spannend part, or going round a ring this step names -- is faded
    // into the calm path first (0.3 s), and only it. A calm step's face to the live eyes may leave one
    // turned a little otherwise: it goes on from there.
    const off = new Set<string>();
    for (const live of this.#live.values()) {
      if (going.has(live.id)) continue;
      const calm = before.get(live.id);
      if (calm === undefined) continue;
      const there = poseOf(calm, live.facts);
      const looping = live.performance?.looping;
      // Off the calm state: elsewhere, or going round when the calm path does not (or not that ring),
      // or standing where the calm path has it going round.
      const ring =
        calm.circle !== undefined
          ? looping === undefined || !sameRing(looping, calm.circle)
          : looping !== undefined;
      if (ring || (strayed ? differs(live.pose, there) : moved(live.pose, there))) off.add(live.id);
    }
    for (const live of this.#live.values()) {
      // A named loop that is not off (it had barely left its start) stops here.
      if (!going.has(live.id) && !off.has(live.id)) live.performance = undefined;
    }
    if (off.size > 0) {
      this.#fade = { fade: new ModelFade(), to: before, only: off, still: named, then: begin };
    } else begin();
  }

  /** "↻ Nog eens": back to the step before's calm end, and this step's entries again. */
  replay(index: number, spannend: boolean): void {
    this.#snap(this.#calmAt(index - 1), false);
    this.enterStep(index, { forward: true, spannend });
    this.#own = true;
  }

  /** The ring a model goes round now, if it does (tests). */
  ringOf(id: string): ModelCircle | undefined {
    return this.#live.get(id)?.performance?.ring;
  }

  /** "Sla over": this pupil's models fade into the step's calm end state. Local: nothing is sent. */
  skip(): boolean {
    if (!this.#run?.skip()) return false;
    // Faded into the calm end state: nothing left elsewhere for the next step.
    this.#strayed = false;
    for (const live of this.#live.values()) {
      live.performance = undefined;
      live.catchUp = undefined;
    }
    this.#fade = { fade: new ModelFade(), to: this.#calmAt(this.#index) };
    this.#veiled = false;
    this.#tell();
    return true;
  }

  /**
   * On by `dt` seconds. `hold`: the teacher's pause or a teleport's fade --
   * nothing moves, plays or sounds. Says whether anything moved (the shadow
   * is drawn again then).
   */
  update(dt: number, hold = false): { moving: boolean } {
    if (this.#disposed || hold) return { moving: false };
    this.#clock += dt;
    this.#input.eye(this.#eye);
    const eye: Ground = [this.#eye.x, this.#eye.z];

    if (this.#run?.update(dt)) {
      this.#strayed = true;
      this.#perform(this.#part ?? []);
      this.#tell();
    }

    const fade = this.#fade;
    if (fade) {
      const { gone, swap } = fade.fade.update(dt);
      for (const live of this.#live.values()) {
        if (live.fitted && (fade.only === undefined || fade.only.has(live.id))) {
          setSeen(live.fitted, 1 - gone);
        }
      }
      if (swap) {
        this.#snap(fade.to, false, {
          ...(fade.only ? { only: fade.only } : {}),
          ...(fade.still ? { still: fade.still } : {}),
        });
        fade.then?.();
      }
      if (fade.fade.done) {
        for (const live of this.#live.values()) if (live.fitted) setSeen(live.fitted, 1);
        if (this.#fade === fade) this.#fade = undefined;
      }
    }

    let moving = fade !== undefined;
    const time = this.#own ? undefined : this.#input.classTime?.();
    for (const live of this.#live.values()) {
      if (live.performance) {
        const before = live.pose;
        const orbit = time ? this.#catchUp(live, this.#orbit(live, time), dt) : undefined;
        live.pose = live.performance.update(dt, eye, orbit);
        if (live.pose.pos[0] !== before.pos[0] || live.pose.pos[1] !== before.pos[1]) moving = true;
        if (live.pose.turn !== before.turn) moving = true;
        this.#place(live);
      } else this.#shade(live);
      // Its clip, the idle loop (staan) whenever nothing else plays: a model is never a statue.
      this.#animate(live, true);
      // A hidden model plays nothing: its clip waits where it is until the model is seen again.
      if (live.fitted?.mixer && live.current && shown(live.object.group)) {
        live.fitted.mixer.update(dt);
        moving = true;
      }
    }

    if (this.#run?.phase === 'part' && this.#allDone()) {
      this.#run.finish();
      this.#tell();
    }

    // Only from a Spannend part on is the veil a question: no footprints worked out otherwise.
    const phase = this.#run?.phase;
    // ADR-084: a model outside the part veils only where it came nearer than its calm path brings it.
    const veiling = phase === 'part' || phase === 'done';
    const inPart = new Set((this.#part ?? []).map((entry) => entry.object));
    this.#veiled =
      veiling &&
      lecternVeiled(
        phase,
        [...this.#live.values()]
          .filter((live) => live.facts !== undefined && shown(live.object.group))
          .map((live) => {
            const part = inPart.has(live.id);
            const calmNear = part ? undefined : this.#calmNearOf(live);
            return {
              facts: live.facts!,
              scale: live.scale,
              footprint: modelFootprint(live.facts!, live.pose.pos, live.pose.turn, {
                scale: live.scale,
                clips: [live.pose.animation],
              }),
              inPart: part,
              ...(calmNear === undefined ? {} : { calmNear }),
            };
          }),
      );

    this.#sounds(dt);
    return { moving };
  }

  dispose(): void {
    this.#disposed = true;
    for (const live of this.#live.values()) {
      live.feet?.dispose();
      live.mouth?.dispose();
      if (live.fitted) {
        this.#input.light?.detach(live.fitted.holder);
        live.fitted.holder.removeFromParent();
        live.fitted.dispose();
      }
    }
    this.#input.sound?.dispose();
  }

  // --- inside -------------------------------------------------------------

  /** Every model's playing done (a loop, not an array: once a frame). */
  #allDone(): boolean {
    for (const live of this.#live.values()) if (live.performance?.done === false) return false;
    return true;
  }

  #tell(): void {
    const run = this.#run;
    const labelOf = (id: string): string | undefined => this.#input.lesson.objects?.[id]?.label;
    this.#input.onSpannend?.({
      phase: run?.phase ?? 'none',
      labels: run && this.#part ? comingClose(this.#part, labelOf) : [],
    });
  }

  /**
   * The calm replay up to step `index`, facing the pupils framing gives (as lesson-schema's
   * `modelStateAt` does): the one replay both the calm poses and a step's moves (its circles) come from.
   */
  #replayAt(index: number): ModelReplay {
    let replay = this.#replays.get(index);
    if (!replay) {
      const pupils = this.#pupils;
      const framed = (i: number): readonly [number, number] | undefined =>
        pupils ? (pupils[i] ? [pupils[i][0], pupils[i][2]] : undefined) : [0, 0];
      replay = replayModels(this.#input.lesson, {
        catalogue: this.#input.catalogue,
        until: index,
        pupil: framed,
      });
      this.#replays.set(index, replay);
    }
    return replay;
  }

  /** The calm path's poses once steps 0..index ran (as declared before the first). */
  #calmNearOf(live: Live): number | undefined {
    if (!this.#calmNear.has(live.id)) {
      const poses = [this.#calmAt(this.#index - 1), this.#calmAt(this.#index)].map((at) =>
        at.get(live.id),
      );
      this.#calmNear.set(live.id, calmNearest(live.facts!, live.scale, poses));
    }
    return this.#calmNear.get(live.id);
  }

  #calmAt(index: number): ReadonlyMap<string, ModelPose> {
    let poses = this.#calm.get(index);
    if (!poses) {
      poses = modelPosesAt(this.#replayAt(index), index);
      this.#calm.set(index, poses);
    }
    return poses;
  }

  /**
   * Every model (or `only` those) where `poses` has it, its clip switched without a cross-fade. A pose
   * going round (`circle`, ADR-054) goes round from there at once -- a jump, Back, a joiner, "Sla over"
   * -- unless the step about to begin names it (`still`).
   */
  #snap(
    poses: ReadonlyMap<string, ModelPose>,
    fade: boolean,
    options: { only?: ReadonlySet<string>; still?: ReadonlySet<string> } = {},
  ): void {
    for (const live of this.#live.values()) {
      if (options.only !== undefined && !options.only.has(live.id)) continue;
      const pose = poses.get(live.id);
      if (!pose) continue;
      live.performance = undefined;
      if (live.catchUp) {
        live.catchUp = undefined;
        if (live.fitted) setSeen(live.fitted, 1);
      }
      live.pose = poseOf(pose, live.facts);
      if (pose.circle && !options.still?.has(live.id)) {
        this.#goRound(live, pose.circle);
        live.ringFrom = this.#namedLast(live.id);
      }
      this.#place(live);
      this.#animate(live, fade);
    }
  }

  /** The last step up to this one whose calm entries name the model: where a ring it is on began. */
  #namedLast(id: string): number {
    const steps = this.#input.lesson.steps;
    for (let k = Math.min(this.#index, steps.length - 1); k > 0; k--) {
      if (steps[k]?.models?.some((entry) => entry.object === id)) return k;
    }
    return 0;
  }

  /**
   * Seconds into its ring's arc the class's time puts a model going round (ADR-054, 20 Oct 2026);
   * undefined when the class is not on this step (it keeps its own time until it is). The lap counts
   * from when the class entered the step that put it on the ring, after the turn-in -- and the fade
   * to the ring's start, when the step named it going round already -- that the calm path has there.
   * When the class was put on a later step at once (a jump), every headset put it at the ring's start
   * then: the lap counts from that step, with no turn-in.
   */
  #orbit(live: Live, time: ClassTime): number | undefined {
    const ring = live.performance?.ring;
    const from = live.ringFrom;
    if (ring === undefined || from === undefined) return undefined;
    const first = time.run[0];
    if (first === undefined || time.run[time.run.length - 1]!.index !== this.#index)
      return undefined;
    if (from < first.index) return time.now - first.at;
    const entered = time.run.find((step) => step.index === from);
    if (entered === undefined) return undefined;
    if (from === first.index && from > 0) return time.now - entered.at;
    const before = this.#calmAt(from - 1).get(live.id);
    const lead =
      (before ? turnSeconds(before.turn, ring.heading) : 0) +
      (before?.circle ? SPANNEND.fadeSeconds : 0);
    return time.now - entered.at - lead;
  }

  /**
   * The class's lap for a model going round, through a fade when it is far from it: until the fade's
   * middle it goes on by its own frames (undefined), from there it is on the class's lap.
   */
  #catchUp(live: Live, orbit: number | undefined, dt: number): number | undefined {
    if (live.catchUp) {
      const fade = live.catchUp;
      const { gone } = fade.update(dt);
      if (live.fitted) setSeen(live.fitted, fade.done ? 1 : 1 - gone);
      if (fade.done) live.catchUp = undefined;
      return fade.swapped ? orbit : undefined;
    }
    const at = live.performance?.orbitTime;
    if (orbit === undefined || at === undefined || this.#fade !== undefined) return orbit;
    if (Math.abs(orbit - at) <= CATCH_UP) return orbit;
    live.catchUp = new ModelFade();
    return undefined;
  }

  /** Round its ring for good, from where it stands: its ring's start (ADR-054). */
  #goRound(live: Live, circle: ModelCircle): void {
    if (!live.facts) return;
    this.#input.eye(this.#eye);
    live.performance = new ModelPerformance({
      facts: live.facts,
      scale: live.scale,
      start: live.pose,
      entry: {
        object: live.id,
        play: circle.gait,
        circle: { direction: circle.direction, rounds: 'loop' },
      },
      eye: [this.#eye.x, this.#eye.z],
      circle,
    });
    live.pose = live.performance.pose;
  }

  /** Step `index`'s calm moves (the same replay as the calm poses): where its circles go (ADR-054). */
  #movesAt(index: number): ReadonlyMap<string, ModelStepReplay> {
    return this.#replayAt(index).steps[index] ?? new Map<string, ModelStepReplay>();
  }

  /** The step's (or its Spannend part's) entries, from where each model is now. */
  #perform(entries: readonly StepModel[]): void {
    this.#input.eye(this.#eye);
    const eye: Ground = [this.#eye.x, this.#eye.z];
    const { lesson, catalogue } = this.#input;
    for (const entry of entries) {
      const live = this.#live.get(entry.object);
      if (!live) continue;
      if (!live.facts) {
        // Not in the library any more: where the calm path has it, nothing drawn.
        const calm = this.#calmAt(this.#index).get(live.id);
        if (calm) live.pose = poseOf(calm, undefined);
        this.#place(live);
        continue;
      }
      const charge =
        entry.walkTo === 'pupil'
          ? spannendWalk(lesson, this.#index, live.id, eye, catalogue, {
              pupils: this.#pupils,
              worlds: this.#input.worlds,
            })
          : undefined;
      // A circle is the calm part's alone (Spannend never goes round, decision 7).
      const circle =
        entry.circle !== undefined && this.#part === undefined
          ? this.#movesAt(this.#index).get(live.id)?.circle
          : undefined;
      live.performance = new ModelPerformance({
        facts: live.facts,
        scale: live.scale,
        start: live.pose,
        entry,
        eye,
        ...(charge ? { charge } : {}),
        ...(circle ? { circle } : {}),
      });
      live.ringFrom = circle ? this.#index : undefined;
      live.pose = live.performance.pose;
      this.#place(live);
      this.#animate(live, true);
    }
  }

  /**
   * The group where the model stands, on the land there (its feet: the GLB's
   * lowest point is at the group's origin, under the middle of its
   * footprint); the GLB and the box turned as it faces. On the stage and
   * apron the land is 0; beyond, a world's slopes rise and fall, and a T. rex
   * put at y = 0 there floated (Pieter, on a Quest 3, 5 Oct 2026). A model `above` the land
   * (ADR-054) stands that much higher, its blob still on the land.
   */
  #place(live: Live): void {
    const { group, body } = live.object;
    const [x, z] = live.pose.pos;
    // ADR-054: a swimmer or flyer `above` the land rides that high over it, the land as it goes.
    group.position.set(x, (this.#input.ground?.(x, z) ?? 0) + live.above, z);
    const yaw = live.pose.turn * DEG;
    body.rotation.y = yaw;
    if (live.fitted) {
      live.fitted.holder.rotation.y = yaw;
      // The soft blob stays on the land under it (the holder is scaled by `scale`).
      live.fitted.blob.position.y = 0.03 - live.above / live.scale;
      this.#shade(live);
    }
  }

  /** The blob or the contact shadow under it, as the floor there takes the sun's shadow or not. */
  #shade(live: Live): void {
    if (!live.fitted) return;
    const [x, z] = live.pose.pos;
    showBlob(live.fitted, x, z, this.#input.unlitFloor?.() === true);
    const contact = live.fitted.contact(false);
    if (contact) {
      // On the land under it, as the blob; a swimmer's or flyer's fainter by its height (ADR-054).
      contact.mesh.position.y = live.fitted.blob.position.y;
      contact.lift(live.above);
    }
  }

  /** The pose's clip on the mixer: cross-faded into (0.35 s; 0.25 into a bite, 0.8 to standing). */
  #animate(live: Live, fade: boolean): void {
    const mixer = live.fitted?.mixer;
    const facts = live.facts;
    if (!mixer || !facts || !live.fitted) return;
    const want = live.pose.animation;
    // The library's clip, or the idle's when the model lacks this one (spec: the engine falls back).
    const name = facts.animations[want]?.clip ?? facts.animations[MODEL_IDLE]?.clip;
    const clip = name === undefined ? undefined : live.fitted.clips.get(name);
    if (!clip) return;
    const current = live.current;
    if (current && current.clip === clip.name) {
      current.action.timeScale = live.pose.timeScale;
      if (current.animation !== want) current.animation = want;
      return;
    }
    const action = mixer.clipAction(clip);
    action.reset();
    action.setLoop(live.pose.loop ? THREE.LoopRepeat : THREE.LoopOnce, Infinity);
    action.clampWhenFinished = !live.pose.loop;
    action.timeScale = live.pose.timeScale;
    action.enabled = true;
    if (current && fade) action.crossFadeFrom(current.action, fadeFor(want), false);
    else if (current) current.action.stop();
    action.play();
    live.current = { clip: clip.name, animation: want, action };
    live.fraction = CLIP_START;
    if (want === MODEL_IDLE) live.cues.restartBreath();
  }

  /** Fetches every model's GLB; a failed one shows nothing and the lesson goes on. */
  #load(): void {
    const load = this.#input.load;
    if (!load) return;
    const all: Promise<void>[] = [];
    for (const live of this.#live.values()) {
      const facts = live.facts;
      if (!facts) continue;
      const one = load(facts).then(
        (loaded) => {
          const fitted = fitModel(loaded, facts, live.scale);
          // Arrived after the lesson ended: let go of it at once.
          if (this.#disposed) {
            fitted.dispose();
            return;
          }
          live.fitted = fitted;
          live.object.group.add(fitted.holder);
          this.#input.light?.attach(fitted.holder, fitted.materials, facts.box.height);
          this.#place(live);
          this.#animate(live, false);
          console.info(
            `model ${facts.slug}: ${facts.triangles} triangles, clips ${[...fitted.clips.keys()].join(' ')}; head ${fitted.head?.name ?? 'none'}`,
          );
        },
        (error: unknown) => console.warn(`model ${facts.slug} could not be loaded`, error),
      );
      all.push(one);
    }
    this.#loaded = Promise.all(all).then(() => undefined);
  }

  /** Every model's GLB arrived or failed (a lesson's picture waits for it, ADR-098). */
  get loaded(): Promise<void> {
    return this.#loaded;
  }

  /**
   * The models that show nothing: their GLB failed or never came, or the catalogue does not know
   * them. Read once `loaded` has settled (a lesson's picture is refused while any is, ADR-098).
   */
  get missing(): number {
    let missing = 0;
    for (const live of this.#live.values()) if (!live.fitted) missing++;
    return missing;
  }

  /** The emitters (≤ 2 a model, ≤ 10) and every take, decoded once. */
  #sound(): void {
    const bank = this.#input.sound;
    if (!bank) return;
    const sounding = [...this.#live.values()].filter(
      (live) => (live.facts?.sounds.length ?? 0) > 0,
    );
    const allowed = new Set(modelsWithEmitters(sounding.map((live) => live.id)));
    const takes = new Set<string>();
    for (const live of sounding) {
      if (!allowed.has(live.id)) continue;
      live.feet = bank.emitter('feet');
      live.mouth = bank.emitter('mouth');
    }
    // Calls first, then steps, then the breath: what the 8 MB budget would leave out last.
    for (const role of ['call', 'step', 'loop'] as const) {
      for (const live of sounding) {
        if (!allowed.has(live.id)) continue;
        for (const sound of live.facts!.sounds) {
          if (sound.role === role) for (const id of sound.media) takes.add(id);
        }
      }
    }
    void bank.load([...takes]);
  }

  /** Where the playing clip is, as a share of it; undefined when nothing runs. */
  #fractionOf(live: Live): number | undefined {
    const action = live.current?.action;
    if (!action?.isRunning()) return undefined;
    const duration =
      action.getClip().duration || (live.facts?.animations[live.pose.animation]?.seconds ?? 1);
    return live.pose.loop
      ? (action.time % duration) / duration
      : Math.min(1, action.time / duration);
  }

  /** Footsteps at the contacts, calls at their seconds, the breath on staan: from where each model is. */
  #sounds(dt: number): void {
    const bank = this.#input.sound;
    if (!bank) return;
    const loud = this.#run?.phase === 'part';
    const fade = this.#fade;
    const distances = new Map<string, number>();
    for (const live of this.#live.values()) {
      const facts = live.facts;
      if (!facts || !live.feet || !live.mouth) continue;
      if (
        !shown(live.object.group) ||
        (fade && (fade.only === undefined || fade.only.has(live.id)))
      ) {
        // Kept up with the clip, so the frame it is heard again does not hear every contact since.
        live.fraction = this.#fractionOf(live) ?? CLIP_START;
        continue;
      }
      const group = live.object.group;
      group.getWorldPosition(this.#at);
      this.#at.y += MODEL_SOUND.feetHeight;
      live.feet.place(this.#at);
      if (live.fitted?.head) live.fitted.head.getWorldPosition(this.#at);
      else
        this.#at.y +=
          (live.fitted?.mouthHeight ?? facts.box.height * 0.85) * live.scale -
          MODEL_SOUND.feetHeight;
      live.mouth.place(this.#at);
      live.mouth.aim(this.#eye, this.#at);
      distances.set(live.id, this.#at.distanceTo(this.#eye));

      const play = (
        emitter: ModelEmitter,
        sound: NonNullable<ReturnType<typeof soundOf>>,
      ): void => {
        const take = live.cues.take(sound);
        if (take)
          bank.play(emitter, take, { db: modelSoundDb(sound, loud), rate: live.cues.pitch() });
      };
      const current = live.current;
      const animation = live.pose.animation;
      const clipFacts = facts.animations[animation];
      const now = this.#fractionOf(live);
      if (current && clipFacts && now !== undefined) {
        const before = live.fraction;
        const steps = soundOf(facts.sounds, animation, 'step');
        if (steps) crossed(before, now, clipFacts.contacts).forEach(() => play(live.feet!, steps));
        const call = soundOf(facts.sounds, animation, 'call');
        if (call?.at !== undefined) {
          const mark = callMark(call.at, clipFacts.seconds);
          if (crossed(before, now, [mark]).length > 0 && live.cues.call(this.#clock)) {
            play(live.mouth, call);
          }
        }
        live.fraction = now;
      }
      const breath = soundOf(facts.sounds, MODEL_IDLE, 'loop');
      // A swim loop named staan and zwemmen (ADR-054, decision 5) breathes as it goes round, so a
      // circling model is heard behind the pupil; a call on its loop sounds at its mark each round.
      const breathing =
        animation === MODEL_IDLE ||
        (clipFacts !== undefined && clipFacts.clip === facts.animations[MODEL_IDLE]?.clip);
      if (breathing && breath && live.cues.breathe(dt)) play(live.mouth, breath);
    }
    this.#panning = mouthPanning(distances, this.#panning);
    for (const [id, panning] of this.#panning) {
      const mouth = this.#live.get(id)?.mouth;
      if (mouth) mouth.panning = panning;
    }
  }
}
